import { useEffect, useRef, useState } from "react";
import { Loader2, Building, Upload, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { AuthImage } from "@/components/kit/AuthImage";
import { companyService } from "@/services/companyService";
import { formatApiErrorDetail } from "@/services/api";
import { CURRENCIES, TIMEZONES } from "@/lib/constants";

export function CompanyDrawer({ open, onOpenChange, company, onSaved }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({});
  const [logoPath, setLogoPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && company) {
      setForm({
        name: company.name || "",
        email: company.email || "",
        phone: company.phone || "",
        website: company.website || "",
        address: company.address || "",
        timezone: company.timezone || "UTC",
        currency: company.currency || "USD",
        low_stock_threshold: company.low_stock_threshold ?? 5,
        status: company.status || "active",
      });
      setLogoPath(company.logo_path || null);
    }
  }, [open, company]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await companyService.uploadLogo(file);
      setLogoPath(res.logo_path);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const nameError = !form.name?.trim() ? "Company name is required" : "";
  const canSave = !nameError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await companyService.update({
        ...form,
        name: form.name.trim(),
        low_stock_threshold: Number(form.low_stock_threshold) || 5,
        logo_path: logoPath,
      });
      toast.success("Company updated");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={Building}
      title="Edit Company"
      description="Update your 3PL company profile and preferences."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="company-drawer-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="company-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Branding">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted/40">
              <AuthImage
                path={logoPath}
                alt="Company logo"
                className="h-full w-full object-contain"
                fallback={<ImageOff className="h-5 w-5 text-muted-foreground" />}
              />
            </div>
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                className="hidden"
                onChange={handleLogo}
                data-testid="company-logo-input"
              />
              <Button variant="outline" size="sm" className="gap-2" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="company-logo-upload">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload logo
              </Button>
              <p className="mt-1.5 text-xs text-muted-foreground">PNG, JPG, WEBP or SVG. Max 5MB.</p>
            </div>
          </div>
        </DrawerSection>

        <DrawerSection title="Business information">
          <Field label="Company Name" required error={nameError}>
            <Input value={form.name || ""} onChange={(e) => set("name")(e.target.value)} data-testid="company-name-input" />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Email">
              <Input type="email" value={form.email || ""} onChange={(e) => set("email")(e.target.value)} data-testid="company-email-input" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone || ""} onChange={(e) => set("phone")(e.target.value)} data-testid="company-phone-input" />
            </Field>
          </div>
          <Field label="Website">
            <Input value={form.website || ""} onChange={(e) => set("website")(e.target.value)} placeholder="https://" data-testid="company-website-input" />
          </Field>
          <Field label="Address">
            <Textarea value={form.address || ""} onChange={(e) => set("address")(e.target.value)} rows={2} data-testid="company-address-input" />
          </Field>
        </DrawerSection>

        <DrawerSection title="Localization & operations">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Timezone">
              <Select value={form.timezone} onValueChange={set("timezone")}>
                <SelectTrigger data-testid="company-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Currency">
              <Select value={form.currency} onValueChange={set("currency")}>
                <SelectTrigger data-testid="company-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Low Stock Threshold" hint="Default alert level for low inventory.">
              <Input type="number" min={0} value={form.low_stock_threshold ?? 5} onChange={(e) => set("low_stock_threshold")(e.target.value)} data-testid="company-threshold-input" />
            </Field>
            <Field label="Company Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="company-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </DrawerSection>
      </div>
    </Drawer>
  );
}
